/**
 * Pin confirmation modal for admin releases.
 */

import * as stylex from "@stylexjs/stylex";
import { useState } from "react";

import { alert, button, modal, spacing, text } from "@animus/primaris";

import type { Release } from "./types.ts";
import { styles } from "./styles.stylex.ts";

interface PinConfirmationModalProps {
  release: Release;
  onConfirm: (reason: string, acknowledgeIncompatibility: boolean) => void;
  onCancel: () => void;
  isLoading: boolean;
  error: string | null;
}

function PinConfirmationModal({
  release,
  onConfirm,
  onCancel,
  isLoading,
  error,
}: PinConfirmationModalProps) {
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  const isIncompatible = release.platform === "mobile" && !release.isCompatible;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onConfirm(reason, acknowledged);
  }

  return (
    <div {...stylex.props(modal.overlay)}>
      <div {...stylex.props(styles.pinModalContainer)}>
        <h2 {...stylex.props(spacing.mb4, text.bold, text.lg, text.white)}>
          Pin {release.platform} to this version?
        </h2>

        <div {...stylex.props(styles.releaseInfo)}>
          <div {...stylex.props(spacing.mb1, styles.sha)}>
            {release.gitSha.slice(0, 7)}
          </div>
          <div {...stylex.props(text.sm, text.muted)}>
            {release.commitMessage ?? "No commit message"}
          </div>
          {release.author && (
            <div {...stylex.props(spacing.mt1, text.xs, text.muted)}>
              by {release.author}
            </div>
          )}
        </div>

        <p {...stylex.props(spacing.mb4, text.sm, text.muted)}>
          This will rollback {release.platform} to this version and freeze
          deployments until you unpin.
        </p>

        {isIncompatible && (
          <div {...stylex.props(styles.warningBox)}>
            <p
              {...stylex.props(spacing.mb2, text.sm, text.medium, text.warning)}
            >
              Compatibility Warning
            </p>
            <p {...stylex.props(text.xs, text.muted)}>
              {release.compatibilityWarning}
            </p>
            <label {...stylex.props(styles.checkbox)}>
              <input
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                type="checkbox"
              />
              <span {...stylex.props(text.sm, text.muted)}>
                I understand the risks
              </span>
            </label>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label {...stylex.props(spacing.mb4)}>
            <span {...stylex.props(spacing.mb1, text.sm, text.muted)}>
              Reason (optional)
            </span>
            <input
              {...stylex.props(styles.input)}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Rollback due to bug in payment flow"
              type="text"
              value={reason}
            />
          </label>

          {error && <div {...stylex.props(alert.error)}>{error}</div>}

          <div {...stylex.props(modal.footer)}>
            <button
              {...stylex.props(button.base, button.secondary, button.small)}
              disabled={isLoading}
              onClick={onCancel}
              type="button"
            >
              Cancel
            </button>
            <button
              {...stylex.props(button.base, button.primary, button.small)}
              disabled={isLoading || (isIncompatible && !acknowledged)}
              type="submit"
            >
              {isLoading ? "Pinning..." : "Pin Version"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { PinConfirmationModal, type PinConfirmationModalProps };
